// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  CohortItem,
  isEventBasedEntry,
  isInlineCohortItem,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryCohortEntry,
  QueryEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {dataGroupIDsEqual} from 'common/util/index';
import {hasSCDFiltersFromOtherDataGroup, hasUserFilters} from 'common/util/custom_events';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  isCohortSegment,
  isCohortSegmentItem,
  isEventSegment,
  isInlineCohortSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {CustomEventDefinition, CustomProperty} from 'common/report/util/saved-entities/types';
import {reporter} from 'common/report/error-reporting';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {
  isAggregateUserFreqByGroupSegmentation,
  isBehaviorSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {Segmentation} from 'common/types/reports/query';
import type {BehaviorMetric} from 'common/report/insights/util/types';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {isScdPropertyName} from 'common/report/util/properties';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {CustomProperties} from 'common/report/stores/custom-properties';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';

import {QueryBuilderSelection} from '../types';

type SanitizeDeps = {
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
};

/**
 * For global data group queries. Removes invalid entries for the selected data group and updates dataGroupId fields on
 * the remaining entries
 */
export function setQueryDataGroup({
  query,
  dataGroupId,
  ...sanitizeDeps
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
} & SanitizeDeps): QueryBuilderSelection {
  const sanitizedQuery = sanitizeQueryForDataGroup({query, dataGroupId, ...sanitizeDeps});
  return setQueryDataGroupProps({query: sanitizedQuery, dataGroupId});
}

function metricHasCustomBehaviorWithInvalidFilters({
  metric,
  customEvents,
  dataGroupId,
}: {
  metric: MetricEntry;
  customEvents: CustomEventDefinition[];
  dataGroupId: string;
}): boolean {
  const customEventIds = new Set<number>();

  metric.behavior.behaviors.forEach((behavior) => {
    const hasEventComparison = !!behavior.behaviors;
    if (hasEventComparison) {
      behavior.behaviors.forEach((beh) => {
        if (beh.type === MetricType.CustomEvent) {
          customEventIds.add(beh.id);
        }
      });
    }
    if (behavior.type === MetricType.CustomEvent) {
      customEventIds.add(behavior.id);
    }
  });

  if (customEventIds.size === 0) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  return Array.from(customEventIds).some((id) => {
    const customEvent = customEvents.find((cb) => cb.id === id);
    return (
      customEvent &&
      (hasUserFilters(customEvent, customEvents) ||
        hasSCDFiltersFromOtherDataGroup({
          customEvent,
          customEvents,
          dataGroupId,
        }))
    );
  });
}

function metricHasInvalidFilterForDataGroup({
  behaviors,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps) {
  return behaviors.some((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return metricHasInvalidFilterForDataGroup({
        behaviors: beh.behaviors,
        dataGroupId,
        metricLevelDataGroups,
        ...sanitizeDeps,
      });
    }
    return beh.filters.some((filter) =>
      filterIsInvalidForDataGroup({
        filter,
        dataGroupId,
        metricLevelDataGroups,
        ...sanitizeDeps,
      }),
    );
  });
}

export function isCustomEventWithUserFilters(
  eventEntry: QueryEventEntry,
  customEvents: CustomEventDefinition[],
): boolean {
  if (!eventEntry.event.custom) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  const customEvent = customEvents.find((ce) => ce.id === eventEntry.event.id);
  return !customEvent || hasUserFilters(customEvent, customEvents);
}

export function cohortEntryIsInvalidForDataGroup(item: QueryCohortEntry | CohortSegment, dataGroupId?: string) {
  if (!isCohortSegmentItem(item)) {
    item = convertCohortItemToCohortSegment(item.event);
  }
  return !dataGroupIDsEqual(getCohortSegmentDataGroupId(item), dataGroupId);
}

export function isClauseInvalidForDataGroup({
  clause,
  dataGroupId,
  customEvents,
}: {
  clause: BehaviorMetric;
  dataGroupId: string;
  customEvents: any[];
}) {
  const {type, name, id, raw_cohort: rawCohort} = clause.behavior;
  const {property} = clause.measurement;
  return (
    (type === MetricType.Cohort &&
      cohortEntryIsInvalidForDataGroup(
        {
          cohort: {
            id,
            name,
            raw_cohort: rawCohort,
          },
        },
        dataGroupId,
      )) ||
    // Detects People property ("All People" is supported by group-level analysis)
    (type === MetricType.People && property && dataGroupId) ||
    // Custom events that are based on user properties
    (dataGroupId &&
      type === MetricType.CustomEvent &&
      hasUserFilters(
        {
          name,
          id,
        } as CustomEventDefinition,
        customEvents,
      ))
  );
}

/** Removes invalid entries for queries with metric-level data groups. This is the equivalent of `setQueryDataGroup` / `sanitizeQueryForDataGroup` for queries with a single data group */
export function sanitizeSelectionForDataGroups({
  query,
  ...sanitizeDeps
}: {query: QueryBuilderSelection} & SanitizeDeps): QueryBuilderSelection {
  const dataGroupIds = new Set(query.entries.map((entry) => getEntryDataGroupId(entry)));
  return {
    ...query,
    entries: sanitizeEventsForDataGroup({queryEntries: query.entries, metricLevelDataGroups: true, ...sanitizeDeps}),
    exclusionSteps: sanitizeEventsForDataGroup({
      queryEntries: query.exclusionSteps,
      metricLevelDataGroups: true,
      ...sanitizeDeps,
    }) as QueryEventEntry[],
    filters: sanitizeFiltersForDataGroups({filters: query.filters, dataGroupIds, ...sanitizeDeps}),
    groupBy: sanitizeGroupByForDataGroups({
      segments: query.groupBy,
      dataGroupIds,
      customProperties: sanitizeDeps.customProperties,
    }),
  };
}

/** For global data group queries */
function sanitizeQueryForDataGroup({
  query,
  dataGroupId,
  ...sanitizeDeps
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
} & SanitizeDeps): QueryBuilderSelection {
  return {
    ...query,
    entries: sanitizeEventsForDataGroup({
      queryEntries: query.entries,
      dataGroupId,
      metricLevelDataGroups: false,
      ...sanitizeDeps,
    }),
    exclusionSteps: sanitizeEventsForDataGroup({
      queryEntries: query.exclusionSteps,
      dataGroupId,
      metricLevelDataGroups: false,
      ...sanitizeDeps,
    }) as QueryEventEntry[],
    filters: query.filters.filter(
      (filter) =>
        !filterIsInvalidForDataGroup({
          filter,
          dataGroupId,
          metricLevelDataGroups: false,
          ...sanitizeDeps,
        }),
    ),
    groupBy: sanitizeGroupByForDataGroup({
      segments: query.groupBy,
      dataGroupId,
      customProperties: sanitizeDeps.customProperties,
    }),
  };
}

function sanitizeMetricBehaviorsForDataGroup({
  behaviors,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps) {
  return behaviors.map((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return {
        ...beh,
        behaviors: sanitizeMetricBehaviorsForDataGroup({
          behaviors: beh.behaviors,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        }),
      };
    }
    const filters = beh.filters.filter(
      (filter) =>
        !filterIsInvalidForDataGroup({
          filter,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        }),
    );
    return {
      ...beh,
      filters,
    };
  });
}

function sanitizeEntryForDataGroup({
  entry,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  entry: QueryEntry | MetricEntry;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): QueryEntry | MetricEntry | null {
  let updatedEntry: QueryEntry | MetricEntry;
  if (metricLevelDataGroups) {
    dataGroupId = getEntryDataGroupId(entry);
  }

  if (isEventBasedEntry(entry) || isUserBasedEntry(entry)) {
    if (isMetricEntry(entry)) {
      const {behavior, measurement} = entry;
      updatedEntry = {
        ...entry,
        behavior: {
          ...behavior,
          behaviors: sanitizeMetricBehaviorsForDataGroup({
            behaviors: behavior.behaviors,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        },
        measurement: {
          ...measurement,
          property: propertyIsInvalidForDataGroup(measurement.property, dataGroupId) ? null : measurement.property,
        },
      };
    } else {
      updatedEntry = {
        ...entry,
        filters: entry.filters.filter((filter) => {
          return !filterIsInvalidForDataGroup({filter, dataGroupId, metricLevelDataGroups, ...sanitizeDeps});
        }),
        property: propertyIsInvalidForDataGroup(entry.property, dataGroupId) ? null : entry.property,
      };
    }
  } else {
    updatedEntry = entry;
  }

  const invalid = queryEntryIsInvalidForDataGroup({
    queryEntry: updatedEntry,
    dataGroupId,
    metricLevelDataGroups,
    ...sanitizeDeps,
  });
  return invalid ? null : updatedEntry;
}

function sanitizeEventsForDataGroup({
  queryEntries,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
  /** retrieve dataGroupId from each entry (instead of the dataGroupId param, which is for a single global data group id) */
  metricLevelDataGroups: boolean;
} & SanitizeDeps): Array<QueryEntry | MetricEntry> {
  // remove invalid event filters and property aggregations
  const sanitizedQueryEntries = queryEntries.map((entry) => {
    return sanitizeEntryForDataGroup({
      entry,
      dataGroupId,
      metricLevelDataGroups,
      ...sanitizeDeps,
    });
  });
  return sanitizedQueryEntries.filter(Boolean);
}

function queryEntryIsInvalidForDataGroup({
  queryEntry,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  queryEntry: QueryEntry | MetricEntry;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): boolean {
  switch (queryEntry.type) {
    // @todo(metrics): Add tests to b2b-metrics.stories.tsx once all cases nailed down.
    case TopLevelMetricType.NewFormulaEntry:
      return queryEntry.referencedMetrics?.some((c) =>
        queryEntryIsInvalidForDataGroup({queryEntry: c, dataGroupId, metricLevelDataGroups, ...sanitizeDeps}),
      );
    case TopLevelMetricType.NewMetricEntry:
      // Simple, Funnels, Retention
      if (queryEntry.behavior.behaviors) {
        // Check if custom event filters are valid.
        const invalidCustomFilters = metricHasCustomBehaviorWithInvalidFilters({
          metric: queryEntry,
          customEvents: sanitizeDeps.customEvents,
          dataGroupId,
        });
        // Check if each behavior's inlined filters are valid.
        // This also makes sure to recursively check for event comparison.
        const invalidInlineFilters = metricHasInvalidFilterForDataGroup({
          behaviors: queryEntry.behavior.behaviors,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        });

        // Check if any of the custom events have user filters.
        let invalidCustomEvents = false;
        if (queryEntry.behavior.type === MetricType.Simple) {
          invalidCustomEvents = queryEntry.behavior.behaviors
            .filter((b) => b.type === MetricType.CustomEvent)
            .some((someEvent) => {
              const customEventDefinition = sanitizeDeps.customEvents.find((ce) => ce.id === someEvent.id);
              // @todo(metrics): Here with `!customEventDefinition` we're essentially validating the existinence of a custom event.
              // Should we be doing this in hasValidMetricBookmarkShowClauses or something as well?
              return !customEventDefinition || hasUserFilters(customEventDefinition, sanitizeDeps.customEvents);
            });
        }

        return invalidCustomFilters || invalidInlineFilters || invalidCustomEvents;
      }

      // @todo(metrics): When we add profile metrics add this.
      // For profile types
      // // Check if the profile property is valid (profilePropertyIsInvalidForDataGroup)

      // @todo(metrics): When we add cohort metrics add this.
      // For cohort type
      // // Just check if data group id matches (cohortEntryIsInvalidForDataGroup)

      return Boolean(dataGroupId);

    // TODO: Once new metric entries are GA'd we can delete all the cases below here.
    case TopLevelMetricType.MetricEntry:
      return (
        (dataGroupId &&
          metricHasCustomBehaviorWithInvalidFilters({
            metric: queryEntry,
            customEvents: sanitizeDeps.customEvents,
            dataGroupId,
          })) ||
        (dataGroupId &&
          metricHasInvalidFilterForDataGroup({
            behaviors: queryEntry.behavior.behaviors,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          })) ||
        (queryEntry.measurement.property && propertyIsInvalidForDataGroup(queryEntry.measurement.property, dataGroupId))
      );
    case ResourceType.Event:
    case SelectorTypeDefinitions.FlowsEvent:
    case SelectorTypeDefinitions.Behavior: {
      return (
        (dataGroupId && isCustomEventWithUserFilters(queryEntry as QueryEventEntry, sanitizeDeps.customEvents)) ||
        queryEntry.filters.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        ) ||
        (queryEntry.property && propertyIsInvalidForDataGroup(queryEntry.property, dataGroupId))
      );
    }
    case ResourceType.Formula: {
      return getResolvedFormulaEntry({
        metricsResource: sanitizeDeps.metricsResource,
        behaviorsResource: sanitizeDeps.behaviorsResource,
        formulaEntry: queryEntry,
      })?.event?.referencedMetrics?.some((c) =>
        isClauseInvalidForDataGroup({
          clause: c as BehaviorMetric,
          dataGroupId,
          customEvents: sanitizeDeps.customEvents,
        }),
      );
    }
    case SelectorTypeDefinitions.CohortGroup:
    case ResourceType.User: {
      const isAllPeople = `$all_people` === queryEntry.event.value;
      return (
        (!isAllPeople &&
          queryEntry.property &&
          profilePropertyIsInvalidForDataGroup({
            profilePropertyDataGroupId: queryEntry.dataGroupId,
            profilePropertyName: queryEntry.property.name,
            dataGroupId,
          })) ||
        queryEntry.filters.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        )
      );
    }
    case ResourceType.Cohort:
      return cohortEntryIsInvalidForDataGroup(queryEntry, dataGroupId);
    case SelectorTypeDefinitions.GroupedEvent:
      return queryEntry.event.some((entry) =>
        queryEntryIsInvalidForDataGroup({
          queryEntry: entry,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        }),
      );
    default:
      // Log unhandled cases and default to marking as invalid for non-null data group
      reporter.error(`Unexpected QueryEntry.type: ${JSON.stringify(queryEntry)}`);
      return !!dataGroupId;
  }
}

function filterIsInvalidForDataGroup({
  filter,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  filter: Partial<PropertyFilter>;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): boolean {
  switch (filter.resourceType) {
    case ResourceType.User: {
      if (filter.customProperty && isBehavioralCustomProperty(filter.customProperty)) {
        const {behavior} = filter.customProperty;
        return queryEntryIsInvalidForDataGroup({
          queryEntry: {
            ...behavior,
            type: SelectorTypeDefinitions.Behavior,
            filters: (behavior.filters ?? []).map((segmentation) => new PropertyFilter({...segmentation})),
          },
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        });
      }

      if (!dataGroupId && isAggregateUserFreqByGroupSegmentation(filter as PropertyFilter)) {
        return true;
      }

      // TODO remove when behaviors are no longer user resource type
      if (isBehaviorSegmentation(filter as Segmentation)) {
        return false;
      }

      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties: sanitizeDeps.customProperties,
        });
      }

      return profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: filter.dataGroupId,
        profilePropertyName: filter.propertyName,
        dataGroupId,
      });
    }
    case ResourceType.Cohort:
      // TODO Delete the return true case when metricLevelDataGroups is turned on everywhere
      // I'm not sure this conditional is needed, but `true` was added to always remove cohorts when switching
      // datagroups to fix a SRFE and I don't want to cause a regression https://github.com/mixpanel/analytics/pull/57453/files
      return metricLevelDataGroups
        ? (filter.filterValue as CohortSegment[]).some((s) => cohortEntryIsInvalidForDataGroup(s, dataGroupId))
        : true;
    case ResourceType.Event:
    case ResourceType.Metric:
    case ResourceType.Other:
    case ResourceType.Formula:
      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties: sanitizeDeps.customProperties,
        });
      }
      return false;
  }
}

function sanitizeFiltersForDataGroups({
  filters,
  dataGroupIds,
  ...sanitizeDeps
}: {
  filters: Array<Partial<PropertyFilter>>;
  dataGroupIds: Set<string | null>;
} & SanitizeDeps): Array<Partial<PropertyFilter>> {
  return filters.filter((filter) => {
    for (const dataGroupId of dataGroupIds) {
      if (filterIsInvalidForDataGroup({filter, dataGroupId, metricLevelDataGroups: true, ...sanitizeDeps})) {
        return false;
      }
    }
    return true;
  });
}

function sanitizeGroupByForDataGroup({
  segments,
  dataGroupId,
  customProperties,
}: {
  segments: Segment[];
  dataGroupId: string;
  customProperties: CustomProperties;
}): Segment[] {
  const filteredSegments = segments.filter(
    (s) =>
      !segmentItemIsInvalidForDataGroup({
        segment: s,
        dataGroupId,
        customProperties,
        metricLevelDataGroups: false,
      }),
  );
  return filteredSegments;
}

function sanitizeGroupByForDataGroups({
  segments,
  dataGroupIds,
  customProperties,
}: {
  segments: Segment[];
  dataGroupIds: Set<string | null>;
  customProperties: CustomProperties;
}): Segment[] {
  // require a non-null data group for PQA queries. we may need to restrict this further to only allow one unique data group
  const allowPQAQueries = dataGroupIds.size && (!dataGroupIds.has(null) || dataGroupIds.size > 1);
  const filteredSegments = segments.filter((s) => {
    for (const dataGroupId of dataGroupIds) {
      if (segmentItemIsInvalidForDataGroup({segment: s, dataGroupId, customProperties, metricLevelDataGroups: true})) {
        return false;
      }
    }
    const isInvalidPQASegment = !allowPQAQueries && isUserSegment(s) && isAggregateUserFreqByGroupSegmentation(s.user);
    return !isInvalidPQASegment;
  });
  return filteredSegments;
}

function segmentItemIsInvalidForDataGroup({
  segment,
  dataGroupId,
  customProperties,
  metricLevelDataGroups,
}: {
  segment: Segment;
  dataGroupId: string;
  customProperties: CustomProperties;
  metricLevelDataGroups: boolean;
}): boolean {
  if (isUserSegment(segment) && (segment.user.customProperty || segment.user.customPropertyId)) {
    return customPropertyIsInvalidForDataGroup({
      dataGroupId,
      customProperty: segment.user.customProperty,
      customPropertyId: segment.user.customPropertyId,
      customProperties,
    });
  } else if (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)) {
    // TODO remove once behaviors are not user resource type
    return [].concat(segment).some((seg) =>
      profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: seg.user.dataGroupId,
        profilePropertyName: seg.user.propertyName,
        dataGroupId,
      }),
    );
  } else if (isCohortSegment(segment)) {
    // TODO Delete the return true case when metricLevelDataGroups is turned on everywhere
    // I'm not sure this conditional is needed, but `true` was added to always remove cohorts when switching
    // datagroups to fix a SRFE and I don't want to cause a regression https://github.com/mixpanel/analytics/pull/57467/files
    return metricLevelDataGroups ? segment.some((s) => cohortEntryIsInvalidForDataGroup(s, dataGroupId)) : true;
  } else {
    // EventSegment, OtherSegment
    if (isEventSegment(segment) && (segment.event.customProperty || segment.event.customPropertyId)) {
      return customPropertyIsInvalidForDataGroup({
        dataGroupId,
        customProperty: segment.event.customProperty,
        customPropertyId: segment.event.customPropertyId,
        customProperties,
      });
    }
    return false;
  }
}

function setEntriesForDataGroup({
  queryEntries,
  dataGroupId,
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
}): Array<QueryEntry | MetricEntry> {
  return queryEntries.map((entry) => {
    if (isMetricEntry(entry)) {
      const {behavior, measurement} = entry;
      if (dataGroupId === null && measurement.property) {
        delete measurement.property[`dataGroupId`];
      }
      return {
        ...entry,
        behavior: {
          ...behavior,
          dataGroupId,
        },
        measurement,
      };
    } else {
      if (dataGroupId === null && entry.property) {
        delete entry.property[`dataGroupId`];
      }
      return {
        ...entry,
        dataGroupId,
      };
    }
  });
}

function setQueryDataGroupProps({
  query,
  dataGroupId,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
}): QueryBuilderSelection {
  return {
    ...query,
    entries: setEntriesForDataGroup({queryEntries: query.entries, dataGroupId}),
    exclusionSteps: query.exclusionSteps.map((entry) => ({...entry, dataGroupId})),
    dataGroupId,
  };
}

function customPropertyIsInvalidForDataGroup({
  dataGroupId,
  customProperty,
  customPropertyId,
  customProperties,
}: {
  dataGroupId: string;
  customProperty: CustomProperty;
  customPropertyId: string | number;
  customProperties: CustomProperties;
}) {
  function isInvalidForDataGroup(customProp: CustomProperty, customPropId: string | number) {
    const composedProperties = customProp
      ? customProp.composedProperties
      : customProperties[customPropId].composedProperties;
    return Object.values(composedProperties).some((prop: Property) => {
      if (prop.customPropertyId) {
        return isInvalidForDataGroup(prop.customProperty, prop.customPropertyId);
      }

      // In group mode, custom prop is invalid if it has user props or group props from other data groups
      if (dataGroupId) {
        return prop.resourceType === ResourceType.User && prop.dataGroupId !== dataGroupId;
      }

      // In user mode, custom prop is valid if it has scd props from other data groups, as those scd properties will fallback to the standard group property.
      return false;
    });
  }

  return isInvalidForDataGroup(customProperty, customPropertyId);
}

export function profilePropertyIsInvalidForDataGroup({
  profilePropertyDataGroupId,
  profilePropertyName,
  dataGroupId,
}: {
  profilePropertyDataGroupId: string;
  profilePropertyName: string | string[];
  dataGroupId: string;
}) {
  if (typeof profilePropertyName === `string` && isScdPropertyName(profilePropertyName)) {
    // SCD properties are invalid for mismatched data groups, except for the empty data group for user queries.
    return dataGroupId && !dataGroupIDsEqual(profilePropertyDataGroupId, dataGroupId);
  } else if (profilePropertyName === `$mp_first_event_time`) {
    // First event time is a behavioral property, and the correct data group id-- global or metric-level, once that is
    // rolled out-- is applied at the query-api level if dataGroupId is blank. If dataGroupId is set, it should match.
    // We could remove this and always return false if we do a bookmark migration to remove dataGroupId from
    // $mp_first_event_time filters/breakdowns on old bookmarks.
    return Boolean(profilePropertyDataGroupId) && !dataGroupIDsEqual(profilePropertyDataGroupId, dataGroupId);
  } else if (dataGroupId) {
    // B2B analysis allows using all data groups' profile properties except for the empty "user" data group
    return !profilePropertyDataGroupId;
  } else {
    // User-level analysis can use all data groups' profile properties
    return false;
  }
}

function convertCohortItemToCohortSegment(item: CohortItem): CohortSegment {
  if (isInlineCohortItem(item)) {
    return {cohort: {...item.value.cohort, negated: false}};
  } else {
    return {
      cohort: {
        id: item.value as number,
        name: item.label,
        negated: false,
        ...(item.data_group_id ? {data_group_id: item.data_group_id} : null),
      },
    };
  }
}

function getCohortSegmentDataGroupId(segment: CohortSegment): string {
  if (isInlineCohortSegment(segment)) {
    return segment.cohort.raw_cohort.data_group_id;
  } else {
    return segment.cohort.data_group_id;
  }
}

function propertyIsInvalidForDataGroup(propertyItem?: PropertySectionItem, dataGroupId?: string): boolean {
  return (
    !!propertyItem &&
    propertyItem.resourceType === ResourceType.User &&
    profilePropertyIsInvalidForDataGroup({
      profilePropertyDataGroupId: propertyItem.dataGroupId,
      profilePropertyName: propertyItem.name || propertyItem.value,
      dataGroupId,
    })
  );
}

export function getMetricDataGroupId(entry: BehaviorMetric | MetricEntry | BehaviorShowClause): string | null {
  if ([MetricType.People, MetricType.Cohort].includes(entry.behavior.type)) {
    return entry.behavior.dataGroupId || null;
  } else {
    return entry.measurement.dataGroupId || null;
  }
}

function getEntryDataGroupId(entry: QueryEntry | MetricEntry): string | null {
  return isMetricEntry(entry) ? getMetricDataGroupId(entry) : entry.dataGroupId || null;
}
